import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LayoutDashboard, Users, IndianRupee, MessageSquare, BookOpen, LogOut, UserCog, Menu, Banknote } from "lucide-react";
import { Toaster } from "sonner";
import { ACADEMY_NAME, LOGO_URL } from "@/lib/brand";

const baseNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/students", label: "Students", icon: Users, testid: "nav-students" },
  { to: "/fees", label: "Fees", icon: IndianRupee, testid: "nav-fees" },
  { to: "/expenses", label: "Expenses", icon: Banknote, testid: "nav-expenses" },
  { to: "/reminders", label: "WhatsApp", icon: MessageSquare, testid: "nav-reminders" },
  { to: "/programs", label: "Programs", icon: BookOpen, testid: "nav-programs" },
];

function NavList({ items, onNavigate }) {
  return (
    <nav className="px-3 py-6 space-y-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={item.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition ${
              isActive ? "bg-lime-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <item.icon size={18} />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarContents({ user, logout, navItems, onNavigate }) {
  return (
    <>
      <div className="px-5 py-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="logo" className="w-12 h-12 object-contain" />
          <div className="min-w-0">
            <div className="text-sm font-bold tracking-tight truncate" style={{ fontFamily: "Chivo" }}>{ACADEMY_NAME}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-400">Admin Panel</div>
          </div>
        </div>
      </div>
      <NavList items={navItems} onNavigate={onNavigate} />
      <div className="p-4 border-t border-slate-800">
        <div className="px-2 py-2">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Signed in as</div>
          <div className="text-sm font-semibold text-white truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-slate-400">{user?.role === "super_admin" ? "Super Admin" : "Staff"}</div>
        </div>
        <Button variant="outline" size="sm" data-testid="logout-button" className="w-full mt-2 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white" onClick={logout}>
          <LogOut size={14} className="mr-2" /> Logout
        </Button>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = user?.role === "super_admin"
    ? [...baseNav, { to: "/staff", label: "Staff", icon: UserCog, testid: "nav-staff" }]
    : baseNav;

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-slate-900 text-slate-100 border-r border-slate-800">
        <SidebarContents user={user} logout={logout} navItems={navItems} />
      </aside>

      {/* Mobile top bar with hamburger */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900 text-white px-3 py-3 flex items-center justify-between gap-2">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800 -ml-1" data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu size={22} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0 bg-slate-900 text-slate-100 border-slate-800" data-testid="mobile-sidebar">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <div className="flex flex-col h-full">
              <SidebarContents user={user} logout={logout} navItems={navItems} onNavigate={() => setMobileOpen(false)} />
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex items-center gap-2 flex-1 justify-center">
          <img src={LOGO_URL} alt="logo" className="w-8 h-8 object-contain" />
          <span className="font-bold text-sm truncate" style={{ fontFamily: "Chivo" }}>{ACADEMY_NAME}</span>
        </div>

        <Button size="sm" variant="ghost" data-testid="mobile-logout" onClick={logout} className="text-white hover:bg-slate-800 -mr-1" aria-label="Logout">
          <LogOut size={16} />
        </Button>
      </div>

      <main className="flex-1 md:pl-0 pt-14 md:pt-0">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
